import { PORTFOLIO_UNKNOWN_ACCOUNT_ID, type PortfolioCashBalance, type PortfolioMetricAvailability } from "@signals/shared";
import { useMemo, type FC } from "react";
import { formatCurrency, formatNumber } from "../_common";
import { SummaryMetricInfo } from "./summary-metric-info";
import { PortfolioMetricStatus } from "./portfolio-metric-status";

const UNKNOWN_ACCOUNT_NAME = "Unknown account";
/** Smallest balance visible at two decimals; floating-point residue stays below it. */
const MIN_VISIBLE_CASH = 0.005;

export interface CashBalancesMetricProps {
  value: string;
  /** Undefined for saved historical snapshots, which keep only the total. */
  cashBalances: PortfolioCashBalance[] | undefined;
  displayCurrency: string;
  accountNames: ReadonlyMap<string, string>;
  closedAccountIds: ReadonlySet<string>;
  availability?: PortfolioMetricAvailability | undefined;
}

interface AccountCashGroup { accountId: string; name: string; balances: PortfolioCashBalance[] }

function groupByAccount(balances: PortfolioCashBalance[], accountNames: ReadonlyMap<string, string>): AccountCashGroup[] {
  const groups = new Map<string, AccountCashGroup>();
  for (const balance of balances) {
    const group = groups.get(balance.accountId) ?? {
      accountId: balance.accountId,
      name: balance.accountId === PORTFOLIO_UNKNOWN_ACCOUNT_ID ? UNKNOWN_ACCOUNT_NAME : accountNames.get(balance.accountId) ?? balance.accountId,
      balances: [],
    };
    group.balances.push(balance);
    groups.set(balance.accountId, group);
  }
  return Array.from(groups.values()).sort((a, b) => a.name.localeCompare(b.name));
}

/** One Info control contains both the definition and the account/currency breakdown. */
export const CashBalancesMetric: FC<CashBalancesMetricProps> = ({ value, cashBalances, displayCurrency, accountNames, closedAccountIds, availability }) => {
  const groups = useMemo(() => cashBalances ? groupByAccount(cashBalances.filter((balance) =>
    Math.abs(balance.amount) >= MIN_VISIBLE_CASH && !closedAccountIds.has(balance.accountId)), accountNames) : [],
  [cashBalances, accountNames, closedAccountIds]);

  return <div>
    <div className="flex items-center gap-1 text-sm text-muted-foreground">
      <span>Cash</span>
      <SummaryMetricInfo metric="cash" buttonLabel="Show cash by account">
        {cashBalances === undefined ? <p className="text-muted-foreground">Cash by account is not stored in saved historical snapshots.</p>
          : groups.length === 0 ? <p className="text-muted-foreground">No cash on open accounts.</p>
          : <div className="space-y-2">{groups.map((group) => <div key={group.accountId}>
            <p className="font-medium">{group.name}</p>
            <ul>{group.balances.map((balance) => <li key={`${balance.accountId}|${balance.currency}`} className="flex justify-between gap-2">
              <span className="text-muted-foreground">{balance.currency}</span>
              <span className={balance.amount < 0 ? "text-red-600 dark:text-red-400" : undefined}>
                {formatCurrency(balance.displayAmount, displayCurrency)}
                {(balance.currency !== displayCurrency || balance.displayAmount == null) && <span className={balance.amount < 0 ? undefined : "text-muted-foreground"}>
                  {" "}({formatNumber(balance.amount)} {balance.currency})
                </span>}
                {balance.amount < 0 && " debt"}
              </span>
            </li>)}</ul>
          </div>)}</div>}
      </SummaryMetricInfo>
    </div>
    <div className="text-lg font-semibold">{value}</div>
    <PortfolioMetricStatus availability={availability} />
  </div>;
};
